import cn from 'classnames'
import SvgPinIcon from 'ext-ui/svg/icon/SvgPinIcon'
import React from 'react'
import InsightsBarChart, {
  InsightsBarChartData,
} from 'src/components/InsightsBarChart/InsightsBarChart'
import InsightsPieChart, {
  InsightsPieChartData,
} from 'src/components/InsightsPieChart/InsightsPieChart'
import { ExtendCurrency } from 'src/enums'
import { useCurrencyFormatter } from 'src/localization/Crncy'
import { useTranslate } from 'src/localization/Trns'
import {
  TileSpecification,
  TileType,
} from 'src/queries/agent/network/agent.models'

import styles from './InsightsTileRenderer.style'
import {
  detectComparisonType,
  mapPeriodComparisonToBarChart,
} from './InsightsTileRenderer.utils'

/**
 * Formats ISO date string to short format (e.g., "Oct 12")
 */
const formatDateShort = (dateStr: string): string => {
  try {
    const date = new Date(dateStr)
    const month = date.toLocaleDateString('en-US', { month: 'short' })
    const day = date.getDate()
    return `${month} ${day}`
  } catch {
    return dateStr
  }
}

export interface InsightsTileRendererProps {
  tileSpec: TileSpecification
  tileData?: { raw: Record<string, any>; mapped: Record<string, any> }
  isLoading?: boolean
  error?: Error | null
  debug?: boolean
  isPinned?: boolean
  onTogglePin?: (shouldPin: boolean) => void
}

/**
 * Maps tile data to InsightsBarChart props based on the tile configuration
 * @param rawData - Raw API response
 * @param mapped - Mapped data from useDynamicTileQuery
 * @param config - Tile mapping configuration
 * @returns Props for InsightsBarChart
 */
const mapToBarChartProps = (
  // TODO: octopod-ai - real types for this
  rawData: Record<string, any>,
  mapped: Record<string, any>,
  config: TileSpecification['config'],
): {
  data: InsightsBarChartData[]
  barKeys?: string[]
  barColors?: string[]
  xAxisLabel?: string
  yAxisLabel?: string
  tooltipFormatter?: (
    value: number,
    name: string,
    item: any,
    index: number,
  ) => React.ReactNode
} => {
  // Try to extract data array from multiple possible locations
  let dataArray: any[] = []

  // First, check if dataArrayKey is specified in config
  if (config.dataArrayKey && mapped.dataArray) {
    dataArray = mapped.dataArray
  }
  // Fallback: Try to extract from raw data structure
  else if (rawData.data?.values) {
    // This handles InsightsView structure: { data: { values: [...] } }
    dataArray = rawData.data.values
  } else if (Array.isArray(rawData.data)) {
    dataArray = rawData.data
  } else if (Array.isArray(rawData.values)) {
    dataArray = rawData.values
  }

  if (!Array.isArray(dataArray) || dataArray.length === 0) {
    return { data: [] }
  }

  // Check if this is period comparison data
  const hasPeriodField = dataArray.length > 0 && dataArray[0].period
  const periodComparison = rawData.summary?.periodComparison

  if (hasPeriodField && periodComparison) {
    // Use period comparison mapper
    const comparisonType = detectComparisonType(periodComparison)

    if (comparisonType) {
      const result = mapPeriodComparisonToBarChart(
        dataArray,
        comparisonType,
        periodComparison,
      )

      return {
        data: result.chartData,
        barKeys: result.barKeys,
        barColors: result.barColors,
        xAxisLabel: undefined,
        yAxisLabel: 'Spend',
      }
    }
  }

  // TODO: octopod-ai - proper types for this data model
  const sortedDataArray = [...dataArray].sort((a: any, b: any) => {
    // Sort by date field if available
    if (a.date && b.date) {
      return new Date(a.date).getTime() - new Date(b.date).getTime()
    }
    // Sort by periodStart if available (period comparison data)
    if (a.periodStart && b.periodStart) {
      return (
        new Date(a.periodStart).getTime() - new Date(b.periodStart).getTime()
      )
    }
    // Sort by period field (prior before current)
    if (a.period && b.period) {
      if (a.period === 'prior' && b.period === 'current') return -1
      if (a.period === 'current' && b.period === 'prior') return 1
    }
    // No sorting needed for categorical data
    return 0
  })

  // For date-based data, fill in missing dates with zero values
  let dataToTransform = sortedDataArray
  const hasDateField = sortedDataArray.length > 0 && sortedDataArray[0].date

  if (hasDateField && sortedDataArray.length > 1) {
    // Get the date range
    const dates = sortedDataArray.map((item: any) => new Date(item.date))
    const minDate = new Date(Math.min(...dates.map((d) => d.getTime())))
    const maxDate = new Date(Math.max(...dates.map((d) => d.getTime())))

    // Create a map of existing dates for quick lookup
    const dateMap = new Map(
      sortedDataArray.map((item: any) => [item.date, item]),
    )

    // Generate all dates in the range
    const allDates: any[] = []
    const currentDate = new Date(minDate)
    while (currentDate <= maxDate) {
      const dateStr = currentDate.toISOString().split('T')[0]
      if (dateMap.has(dateStr)) {
        allDates.push(dateMap.get(dateStr))
      } else {
        // Create a zero-value entry for missing dates
        // Copy structure from first item but set all numeric values to 0
        const firstItem = sortedDataArray[0]
        const zeroItem: any = { date: dateStr }

        Object.keys(firstItem).forEach((key) => {
          if (
            typeof firstItem[key] === 'number' &&
            key !== 'transactionCount'
          ) {
            zeroItem[key] = 0
          }
        })

        allDates.push(zeroItem)
      }

      currentDate.setDate(currentDate.getDate() + 1)
    }

    dataToTransform = allDates
  }

  // Transform dataToTransform to InsightsBarChartData format
  // Handle different data structures:
  // 1. Time series: { date, period, usdSpendCents, ... }
  // 2. Category data: { label, usdSpendCents, ... }
  const chartData: InsightsBarChartData[] = dataToTransform.map((item: any) => {
    // Determine label based on available fields
    let label: string
    if (item.date) {
      // Time series with explicit date field
      label = formatDateShort(item.date)
    } else if (item.period && item.periodStart && item.periodEnd) {
      // Period comparison data (e.g., "current" vs "prior")
      const periodLabel =
        item.period === 'current'
          ? 'Current'
          : item.period === 'prior'
            ? 'Prior'
            : item.period
      label = `${periodLabel} (${formatDateShort(item.periodStart)} - ${formatDateShort(item.periodEnd)})`
    } else if (item.period) {
      // Period without dates (e.g., just "current" or "prior")
      label =
        item.period === 'current'
          ? 'Current Period'
          : item.period === 'prior'
            ? 'Prior Period'
            : item.period
    } else {
      // Categorical data
      label = item.label || item.name || item.category || 'Unknown'
    }

    const dataPoint: InsightsBarChartData = {
      label,
    }

    // Add all numeric fields as potential bar values
    Object.entries(item).forEach(([key, value]) => {
      if (typeof value === 'number') {
        // Convert cents to dollars for display using explicit suffix check
        const isCentsField = key.endsWith('Cents') || key.endsWith('InCents')
        if (isCentsField) {
          const dollarKey = key.replace(/(?:In)?Cents$/, '')
          dataPoint[dollarKey] = value / 100
        } else if (
          key !== 'label' &&
          key !== 'name' &&
          key !== 'category' &&
          key !== 'transactionCount'
        ) {
          dataPoint[key] = value
        }
      }
    })

    return dataPoint
  })

  // Determine barKeys - what fields to render as bars
  // Look for value keys in the first data point
  const sampleItem = chartData[0]
  let barKeys = Object.keys(sampleItem).filter(
    (key) => key !== 'label' && typeof sampleItem[key] === 'number',
  )

  // Filter out keys where all values are 0
  barKeys = barKeys.filter((key) => {
    return chartData.some((item) => item[key] !== 0)
  })

  return {
    data: chartData,
    barKeys: barKeys.length > 0 ? barKeys : undefined,
    xAxisLabel: undefined,
    yAxisLabel: 'Spend',
  }
}

const InsightsTileRenderer: React.FC<InsightsTileRendererProps> = ({
  tileSpec,
  tileData,
  isLoading = false,
  error = null,
  debug = false,
  isPinned = false,
  onTogglePin,
}) => {
  const translate = useTranslate()
  const formatCurrency = useCurrencyFormatter()

  const handlePinClick = () => {
    if (!onTogglePin) {
      return
    }
    onTogglePin(!isPinned)
  }

  const pinAriaLabel = onTogglePin
    ? translate(isPinned ? 'Unpin insight' : 'Pin insight')
    : undefined

  const renderTileContent = () => {
    // Show loading state
    if (isLoading) {
      return (
        <div className="tile-content loading">
          {translate('Loading data...')}
        </div>
      )
    }

    // Show error state
    if (error) {
      return (
        <div className="tile-content error">
          {translate('Error loading data:')} {error.message}
        </div>
      )
    }

    // Show empty state if no data
    if (!tileData) {
      return (
        <div className="tile-content empty">
          {translate('No data available')}
        </div>
      )
    }

    switch (tileSpec.type) {
      case TileType.NUMBER_CARD: {
        const summary = tileData.raw.summary
        const periodComparison = summary?.periodComparison
        const values = tileData.raw.data?.values || []

        // Check if this is period comparison data
        const hasPeriodComparison =
          periodComparison && values.length > 0 && values[0].period

        if (!hasPeriodComparison) {
          return (
            <div className="tile-content empty">
              {translate('No data available')}
            </div>
          )
        }
        // Render period comparison card with new layout
        const { totalUsdSpendCents, totalCadSpendCents } = summary

        return (
          <div className="tile-content number-card flex-column">
            {/* Gradient border at top */}
            <div className="gradient-border" />

            <div className={'flex-row gap-4'}>
              <div className="period-column">
                <div className="label">{translate('Total Spend')}</div>
                <div className="number-accent">
                  {formatCurrency({ amount: totalUsdSpendCents / 100 })}
                </div>
              </div>
              {periodComparison.usdPercentageChange !== null && (
                <div className="period-column">
                  <div className="label">
                    {translate('Spend vs. Prior Period')}
                  </div>
                  <div className="number-large">
                    <span>
                      {periodComparison.usdPercentageChange >= 0 ? '+' : ''}
                      {periodComparison.usdPercentageChange.toFixed(1)}
                      {'%'}
                    </span>
                  </div>
                  <div className="period-dates">
                    {formatCurrency({
                      amount: periodComparison.priorUsdSpendCents / 100,
                    })}{' '}
                    {translate('in prior period')}
                  </div>
                </div>
              )}
            </div>

            {/* CAD Row (if applicable) */}
            {totalCadSpendCents > 0 && (
              <div className={'flex-row gap-4'}>
                <div className="period-column">
                  <div className="label">{translate('Total CAD Spend')}</div>
                  <div className="number-accent">
                    {formatCurrency({
                      amount: totalCadSpendCents / 100,
                      currency: ExtendCurrency.Cad,
                    })}
                  </div>
                </div>
                {periodComparison.cadPercentageChange !== null && (
                  <div className="period-column">
                    <div className="label">
                      {translate('Spend vs. Prior Period')}
                    </div>
                    <div className="number-large">
                      <span className="percentage">
                        {periodComparison.cadPercentageChange >= 0 ? '+' : ''}
                        {periodComparison.cadPercentageChange.toFixed(1)}
                        {'%'}
                      </span>
                    </div>
                    <div className="period-dates">
                      {formatCurrency({
                        amount: periodComparison.priorCadSpendCents / 100,
                        currency: ExtendCurrency.Cad,
                      })}{' '}
                      {translate('in prior period')}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )
      }
      case TileType.GRAPH: {
        const barChartProps = mapToBarChartProps(
          tileData.raw,
          tileData.mapped,
          tileSpec.config,
        )

        if (barChartProps.data.length === 0) {
          return (
            <div className="tile-content empty">
              {translate('No data available for graph')}
            </div>
          )
        }

        return (
          <div className="tile-content graph flex-column gap-4">
            <InsightsBarChart
              {...barChartProps}
              height={300}
              tooltipFormatter={(value, name, item) => {
                // Enhanced tooltip for period comparison data
                if (name.includes('prior') || name.includes('current')) {
                  const isPrior = name.includes('prior')
                  const isCad = name.includes('Cad')
                  const currency = isCad
                    ? ExtendCurrency.Cad
                    : ExtendCurrency.Usd
                  const periodLabel = isPrior
                    ? translate('Prior')
                    : translate('Current')
                  const currencyLabel = isCad ? 'CAD' : 'USD'

                  // Get specific date for this data point from payload
                  const payload = item.payload as any
                  const specificDate = isPrior
                    ? payload?.priorDate
                    : payload?.currentDate

                  // Format date with year (e.g., "Oct 24 '25")
                  const formatDateWithYear = (dateStr: string) => {
                    if (!dateStr) return ''
                    try {
                      const date = new Date(dateStr)
                      const month = date.toLocaleDateString('en-US', {
                        month: 'short',
                      })
                      const day = date.getDate()
                      const year = date.getFullYear().toString().slice(-2)
                      return `${month} ${day} '${year}`
                    } catch {
                      return dateStr
                    }
                  }

                  const formattedDate = specificDate
                    ? formatDateWithYear(specificDate)
                    : ''

                  return (
                    <div className="graph-tooltip">
                      <div className="tooltip-header">
                        {translate('${period} Period (${currency})', {
                          period: periodLabel,
                          currency: currencyLabel,
                        })}
                      </div>
                      {formattedDate && (
                        <div className="tooltip-date-range">
                          {formattedDate}
                        </div>
                      )}
                      <div className="tooltip-value">
                        {formatCurrency({ amount: value, currency })}
                      </div>
                    </div>
                  )
                }

                // Default formatting for non-period data
                return formatCurrency({ amount: value })
              }}
            />
          </div>
        )
      }
      case TileType.LEADERBOARD: {
        const values = tileData.raw.data?.values || []
        const summary = tileData.raw.summary

        // Only show error message if we don't have a valid response
        if (values.length === 0) {
          return (
            <div className="tile-content empty">
              {translate('No leaderboard data available')}
            </div>
          )
        }

        // Check if we have CAD spend data
        const hasCadSpend = values.some(
          (item: any) => item.cadSpendCents && item.cadSpendCents > 0,
        )

        // Transform values to InsightsPieChartData format for USD
        const usdPieChartData: InsightsPieChartData[] = values
          .filter((item: any) => item.usdSpendCents && item.usdSpendCents > 0)
          .map((item: any) => ({
            label: item.label || item.name || 'Unknown',
            name: item.label || item.name || 'Unknown',
            value: item.usdSpendCents || 0,
            usdSpendCents: item.usdSpendCents || 0,
            category: item.category,
          }))

        // Transform values to InsightsPieChartData format for CAD (if present)
        const cadPieChartData: InsightsPieChartData[] = hasCadSpend
          ? values
              .filter(
                (item: any) => item.cadSpendCents && item.cadSpendCents > 0,
              )
              .map((item: any) => ({
                label: item.label || item.name || 'Unknown',
                name: item.label || item.name || 'Unknown',
                value: item.cadSpendCents || 0,
                usdSpendCents: item.cadSpendCents || 0, // Using usdSpendCents key for consistency with tooltip formatter
                category: item.category,
              }))
          : []

        return (
          <div className="tile-content leaderboard flex-column gap-4">
            {summary &&
              (summary.totalUsdSpendCents || summary.totalCadSpendCents) && (
                <div className="section insights-summary gap-3">
                  <div className="flex-row gap-4">
                    {summary.totalUsdSpendCents > 0 && (
                      <div className="flex-column gap-1 flex-1">
                        <div className="label">
                          {translate('Total USD Spend')}
                        </div>
                        <div className="number-large">
                          {formatCurrency({
                            amount: summary.totalUsdSpendCents / 100,
                          })}
                        </div>
                      </div>
                    )}
                    {summary.totalCadSpendCents > 0 && (
                      <div className="flex-column gap-1 flex-1">
                        <div className="label">
                          {translate('Total CAD Spend')}
                        </div>
                        <div className="number-large">
                          {formatCurrency({
                            amount: summary.totalCadSpendCents / 100,
                            currency: ExtendCurrency.Cad,
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            <div className={'chart-wrapper'}>
              {hasCadSpend ? (
                <div className="flex-row gap-4">
                  {usdPieChartData.length > 0 && (
                    <div className="flex-column gap-2 flex-1">
                      <div className="item-title">
                        {translate('USD Spend Breakdown')}
                      </div>
                      <InsightsPieChart
                        data={usdPieChartData}
                        minHeight={300}
                        valueKey="usdSpendCents"
                      />
                    </div>
                  )}
                  {cadPieChartData.length > 0 && (
                    <div className="flex-column gap-2 flex-1">
                      <div className="item-title">
                        {translate('CAD Spend Breakdown')}
                      </div>
                      <InsightsPieChart
                        data={cadPieChartData}
                        minHeight={300}
                        valueKey="usdSpendCents"
                        tooltipFormatter={(value, name, item, index) => {
                          const total = cadPieChartData.reduce(
                            (sum, d) => sum + (d.value as number),
                            0,
                          )
                          const percent =
                            total > 0
                              ? ((value / total) * 100).toFixed(1)
                              : '0.0'
                          const formattedAmount = formatCurrency({
                            amount: value / 100,
                            currency: ExtendCurrency.Cad,
                          })
                          return `${formattedAmount} (${percent}%)`
                        }}
                      />
                    </div>
                  )}
                </div>
              ) : (
                <InsightsPieChart
                  data={usdPieChartData}
                  minHeight={300}
                  valueKey="usdSpendCents"
                />
              )}
            </div>
          </div>
        )
      }
      default:
        return <></>
    }
  }

  return (
    <div css={styles} className="InsightsTileRenderer">
      <div className="tile-header">
        <div className="tile-title">{tileSpec.title}</div>
        {onTogglePin && (
          <button
            type="button"
            className={cn('tile-pin-button', { pinned: isPinned })}
            onClick={handlePinClick}
            aria-label={pinAriaLabel}
            aria-pressed={isPinned}
          >
            <SvgPinIcon filled={isPinned} aria-hidden />
          </button>
        )}
      </div>
      <div className="divider" />
      {renderTileContent()}
    </div>
  )
}

export default InsightsTileRenderer
